import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import LoadingSpinner from "../LoadingSpinner";
import Grid from "../Grid";
import { getDropdownData, getTextByValueAndKey } from "../../api/keyvalue";
import { operationValidation } from "../validations/vehicleCreation";
import ButtonRenderer from "../renderer/ButtonRenderer";
import Input from "../input/Input";
import Dropdown from "../input/Dropdown";
import Button from "../input/Button";
import { AUTH_TYPES, FORMS, MODULES } from "../../const";
import { isNumber } from "../../helper";
import DeleteButtonRenderer from "../renderer/DeleteButtonRenderer";

const FORM = FORMS.VEHICLE.OPERATIONS.form;

const VehicleOperations = forwardRef(
  ({ dropdown, isLoading, currentValues, editMode }, ref) => {
    const [isAddLoading, setIsAddLoading] = useState(false);
    const [currentOperation, setCurrentOperation] = useState({});
    const [buttonClicked, setButtonClicked] = useState(false);
    const [deletedList, setDeletedList] = useState([]);

    const [categorySelected, setCategorySelected] = useState(false);
    const [typeOpt, setTypeOpt] = useState([]);
    const [typeOptLoading, setTypeOptLoading] = useState(false);

    const [vpyCount, setVpyCount] = useState(2);
    const [vpyOperations, setVpyOperations] = useState([]);
    const [currentErrors, setCurrentErrors] = useState({});
    const [operations, setOperations] = useState({
      vpyCode: "",
      vpyCategory: "",
      vpyCategoryValue: "",
      vpyType: "",
      vpyTypeValue: "",
      vpyDescription: "",
      vpyPrimary: false,
      vpyStatus: 2,
      vpyActive: 1,
    });

    useEffect(() => {
      (async () => {
        if (operations.vpyCategory !== "") {
          setTypeOptLoading(true);
          const vpyData = await getDropdownData(
            "vpy_type",
            operations.vpyCategory.toString(),
          );
          setTypeOpt(vpyData);
          setTypeOptLoading(false);
        }
      })();
    }, [categorySelected, operations.vpyCategory]);

    useEffect(() => {
      (async () => {
        if (editMode) {
          let currentOperations = [];
          let id = 2;
          for (let currentValue of currentValues) {
            currentOperations.push({
              ...currentValue,
              id: id,
              vpyVehicle: currentValue.vpyVehicleId,
            });
            id++;
          }

          setVpyCount(id);
          setVpyOperations(currentOperations);
        }
      })();
    }, [editMode, currentValues]);

    useEffect(() => {
      if (buttonClicked) {
        setOperations({
          vpyCode: "",
          vpyCategory: "",
          vpyCategoryValue: "",
          vpyType: "",
          vpyTypeValue: "",
          vpyDescription: "",
          vpyPrimary: false,
          vpyStatus: 2,
          vpyActive: 1,
        });
        setCurrentErrors({});
        setButtonClicked(false);
        setCurrentOperation({});
      }
    }, [buttonClicked]);

    useEffect(() => {
      if (Object.keys(currentOperation).length > 0) {
        setOperations({
          ...currentOperation,
          // vpyCategoryValue: editMode ? currentOperation.vpyCategoryValue : "",
          // vpyTypeValue: editMode ? currentOperation.vpyTypeValue : "",
        });
      }
    }, [editMode, currentOperation]);

    useImperativeHandle(ref, () => ({
      resetGrid: () => {
        setVpyOperations([]);
      },
      // getData: () => (vpyOperations.length > 0 ? vpyOperations[0] : operations),
      getData: () => {
        if (editMode) {
          const updateState = {
            modified: [],
            unmodified: [],
            created: [],
            deleted: deletedList,
          };

          vpyOperations.forEach((vpy) => {
            if (vpy.vpyId && vpy.vpyId !== null) {
              if (vpy.modified) {
                updateState.modified.push(vpy);
              } else {
                updateState.unmodified.push(vpy);
              }
            } else {
              updateState.created.push(vpy);
            }
          });

          return updateState;
        } else {
          return vpyOperations;
        }
      },
    }));

    const booleanOptions = [
      { key: "1", value: true, text: "Yes" },
      { key: "0", value: false, text: "No" },
    ];

    const onAddClick = async () => {
      setIsAddLoading(true);

      const operationsErrors = operationValidation(operations);
      if (Object.keys(operationsErrors).length === 0) {
        const vpyCategoryValue = await getTextByValueAndKey(
          "vpy_category",
          operations.vpyCategory,
        );
        const vpyTypeValue = await getTextByValueAndKey(
          "vpy_type",
          operations.vpyType,
        );
        if (!operations.vpyId || operations.vpyId === null) {
          if (!operations.id || operations.id === null) {
            setVpyOperations((prevState) => [
              ...prevState,
              {
                ...operations,
                vpyCategoryValue,
                vpyTypeValue,
                id: vpyCount,
              },
            ]);
            setVpyCount((prevState) => ++prevState);
          } else {
            setVpyOperations((prevState) =>
              prevState.filter((item) => item.id !== operations.id),
            );

            setVpyOperations((prevState) => [
              ...prevState,
              {
                ...operations,
                vpyCategoryValue,
                vpyTypeValue,
              },
            ]);
          }
        } else {
          setVpyOperations((prevState) =>
            prevState.filter((item) => item.vpyId !== operations.vpyId),
          );

          setVpyOperations((prevState) => [
            ...prevState,
            {
              ...operations,
              vpyCategoryValue,
              vpyTypeValue,
              modified: true,
            },
          ]);
        }

        setButtonClicked(true);
      }

      setCurrentErrors(operationsErrors);
      document.querySelector("#vehicle-details-form").classList.add("error");
      setIsAddLoading(false);
    };

    const handleCategoryChange = (e, data) => {
      const selectedCategory = data.value;

      setOperations((prev) => ({
        ...prev,
        vpyCategory: selectedCategory,
      }));
      setCategorySelected(true);
    };

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <h4 className="ui dividing header">Operations</h4>
              <div className="three fields">
                <Dropdown
                  required={true}
                  label="Operation Category"
                  error={currentErrors.vpyCategory}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vpy_category"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vpyCategory: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.vpyCategoryOpt}
                  placeholder="Operation Category"
                  style={{ height: "20px" }}
                  value={operations.vpyCategory}
                  onChange={handleCategoryChange}
                />
                <Dropdown
                  required={true}
                  label=" Type"
                  error={currentErrors.vpyType}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vpy_type"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vpyType: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={typeOpt}
                  placeholder={typeOptLoading ? "Loading ..." : "Type"}
                  style={{ height: "20px" }}
                  disabled={!categorySelected || typeOptLoading}
                  value={operations.vpyType}
                  onChange={(e, data) =>
                    setOperations((prev) => ({
                      ...prev,
                      vpyType: data.value,
                    }))
                  }
                />
                <Input
                  label="Operation Code"
                  error={currentErrors.vpyCode}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vpy_code"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vpyCode: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="code"
                  placeholder="Operation Code"
                  value={operations.vpyCode}
                  disabled={true}
                />
              </div>
              <div className="two fields">
                <Input
                  label="Description"
                  error={currentErrors.vpyDescription}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vpy_description"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vpyDescription: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="description"
                  placeholder="Description"
                  value={operations.vpyDescription}
                  onChange={(e) =>
                    setOperations((prev) => ({
                      ...prev,
                      vpyDescription: e.target.value,
                    }))
                  }
                />
                {/* <Dropdown
                  label=" Active"
                  error={currentErrors.vpyActive}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vpy_active"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vpyActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.activeOpt}
                  style={{ height: "20px" }}
                  placeholder="Active"
                  value={operations.vpyActive}
                  onChange={(e, data) =>
                    setOperations((prev) => ({
                      ...prev,
                      vpyActive: data.value,
                    }))
                  }
                /> */}
              </div>
              <div className="two fields">
                {/* <div className={`field ${errors.vpyStatus ? "error" : ""}`}>
                <label>Status</label>
                <Select
                  clearable
                  options={dropdown.statusOpt}
                  placeholder="Status"
                  style={{ height: "20px" }}
                  value={operations.vpyStatus}
                  onChange={(e, data) =>
                    setOperations((prev) => ({
                      ...prev,
                      vpyStatus: data.value,
                    }))
                  }
                />
                <small style={{ color: "#9f3a38" }}>{`${
                  errors.vpyStatus ? errors.vpyStatus : ""
                }`}</small>
              </div> */}
                <Dropdown
                  label=" Primary (Yes/No"
                  error={currentErrors.vpyPrimary}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="vpy_primary"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { vpyPrimary: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={booleanOptions}
                  style={{ height: "20px" }}
                  placeholder="Primary (Yes/No)"
                  value={operations.vpyPrimary}
                  onChange={(e, data) =>
                    setOperations((prevState) => ({
                      ...prevState,
                      vpyPrimary: data.value,
                    }))
                  }
                />
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  template="reset"
                  onClick={(e) => {
                    setButtonClicked(true);
                  }}
                />
                <Button
                  template="addOrSave"
                  authenticated={true}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={`${FORM}`}
                  create={Object.keys(currentOperation).length === 0}
                  onClick={onAddClick}
                  isLoading={isAddLoading}
                />
              </div>
              <Grid
                heading="Vehicle Operation List"
                config={[
                  {
                    headerName: "Operation Category",
                    field: "vpyCategoryValue",
                  },
                  { headerName: "Operation Type", field: "vpyTypeValue" },
                  {
                    headerName: "Operation Description",
                    field: "vpyDescription",
                  },
                  {
                    field: "Edit",
                    filter: true,
                    cellRenderer: ButtonRenderer,
                    cellRendererParams: (params) => ({
                      consider: AUTH_TYPES.CREATE,
                      clickHandler: () => {
                        setCurrentOperation(params.data);
                      },
                      label: "Edit",
                      authenticated: true,
                      form: `${FORM}`,
                      module: MODULES.Master,
                      editMode: editMode,
                    }),
                  },
                  {
                    field: "Delete",
                    filter: true,
                    cellRenderer: DeleteButtonRenderer,
                    cellRendererParams: (params) => ({
                      consider: AUTH_TYPES.DELETE,
                      clickHandler: () => {
                        if (params.data.vpyId && params.data.vpyId !== null) {
                          setDeletedList((prevState) => [
                            ...prevState,
                            params.data.vpyId,
                          ]);
                        }
                        setVpyOperations((prevOperations) =>
                          prevOperations.filter(
                            (operation) => operation.id !== params.data.id,
                          ),
                        );
                      },
                        message:
                          "Are you sure you want to delete this Vehicle Operation?",
                      label: "Delete",
                      className: "ui red button",
                      authenticated: true,
                      form: `${FORM}`,
                      module: MODULES.Master,
                      editMode: params.data.vpyId !== null && isNumber(params.data.vpyId),
                    }),
                  },
                ]}
                content={vpyOperations}
              />
            </div>
          </form>
          </>
        )}
      </>
    );
  },
);

export default VehicleOperations;
